import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiCalendar,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiCheckSquare,
  FiClock,
  FiAlertCircle,
  FiList,
  FiSearch,
  FiBarChart2,
  FiPackage,
  FiTrendingUp,
  FiInfo,
} from "react-icons/fi";

import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import { withAuth } from "../../../components/HOC/withAuth";

import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import CardInfo from "../../../components/CustomUI/CardInfo";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import Select from "../../../components/BaseUI/Select";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Panel,
} from "../../../components/BaseUI/Tabs";
import Checkbox from "../../../components/BaseUI/Checkbox";

import styles from "./ProductionPlanning.module.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const STATUS_OPTIONS = [
  { value: "planned", label: "Planlandı" },
  { value: "in_progress", label: "Devam Ediyor" },
  { value: "completed", label: "Tamamlandı" },
  { value: "cancelled", label: "İptal Edildi" },
];

const STATUS_COLORS = {
  planned: "#3b82f6",
  in_progress: "#f59e0b",
  completed: "#10b981",
  cancelled: "#ef4444",
};

const STATUS_LABELS = {
  planned: "Planlandı",
  in_progress: "Devam Ediyor",
  completed: "Tamamlandı",
  cancelled: "İptal Edildi",
};

const EMPTY_FORM = {
  title: "",
  partId: "",
  productionLineId: "",
  plannedQuantity: "",
  startDate: "",
  endDate: "",
  operatorNote: "",
  status: "planned",
};

const PLANS_COLUMNS = [
  { name: "Plan Adı", dataType: "text", id: 0 },
  { name: "Parça", dataType: "text", id: 1 },
  { name: "Üretim Hattı", dataType: "text", id: 2 },
  { name: "Planlanan", dataType: "number", id: 3 },
  { name: "Gerçekleşen", dataType: "number", id: 4 },
  { name: "İlerleme", dataType: "text", id: 5, sortable: false },
  { name: "Tarih Aralığı", dataType: "text", id: 6 },
  { name: "Durum", dataType: "text", id: 7, sortable: false },
  { name: "İşlemler", dataType: "text", id: 8, sortable: false },
];

const FEASIBILITY_COLUMNS = [
  { name: "Parça", dataType: "text", id: 0 },
  { name: "Kaynak", dataType: "text", id: 1 },
  { name: "Maks. Üretilebilir", dataType: "number", id: 3 },
  { name: "Eksik Bileşen", dataType: "number", id: 4 },
];

const BOM_SOURCE_LABELS = {
  finalProduct: "Son Ürün",
  partMachine: "Üretim",
  partAssembly: "Montaj",
};

const SHORTAGE_COLUMNS = [
  { name: "Bileşen", dataType: "text", id: 0, sortable: false },
  { name: "Kod", dataType: "text", id: 1, sortable: false },
  { name: "Tüketim / Adet", dataType: "number", id: 2, sortable: false },
  { name: "Toplam İhtiyaç", dataType: "number", id: 3, sortable: false },
  { name: "Mevcut Stok", dataType: "number", id: 4, sortable: false },
  { name: "Eksik Miktar", dataType: "number", id: 5, sortable: false },
  { name: "Durum", dataType: "text", id: 6, sortable: false },
];

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => (
  <span
    className={styles.statusBadge}
    style={{
      background: STATUS_COLORS[status] + "22",
      color: STATUS_COLORS[status],
    }}
  >
    {STATUS_LABELS[status] || status}
  </span>
);

// ─── Progress Bar ─────────────────────────────────────────────────────────────
const ProgressBar = ({ value, max, color = "#10b981" }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={styles.progressWrap}>
      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className={styles.progressPct}>{pct}%</span>
    </div>
  );
};

// ─── BOM Detail Table (reusable inline) ──────────────────────────────────────
const BomDetailTable = ({ bomLines, styles: s, indent = 0 }) => (
  <div
    className={s.bomDetail}
    style={indent ? { marginLeft: indent } : undefined}
  >
    <div className={s.bomDetailTitle}>
      <FiList size={14} /> Malzeme Listesi (BOM) — 1 adet üretim için
    </div>
    <table className={s.bomTable}>
      <thead>
        <tr>
          <th>Bileşen</th>
          <th>Tüketim / Adet</th>
          <th>Mevcut Stok</th>
          <th>Karşılanan Üretim</th>
          <th>Durum</th>
        </tr>
      </thead>
      <tbody>
        {bomLines.map((bline, i) => (
          <tr key={i}>
            <td>
              <div style={{ fontWeight: 600 }}>{bline.partName}</div>
              <div
                style={{ fontSize: 11, color: "var(--m-text-subtle, #9ca3af)" }}
              >
                {bline.partCode}
              </div>
            </td>
            <td>{bline.consumptionRatio} / adet</td>
            <td>
              <strong
                style={{
                  color: bline.availableQty > 0 ? undefined : "#ef4444",
                }}
              >
                {bline.availableQty}
              </strong>
            </td>
            <td>
              <strong
                style={{
                  color: bline.coverageUnits > 0 ? "#10b981" : "#ef4444",
                }}
              >
                {bline.coverageUnits} adet
              </strong>
            </td>
            <td>
              {bline.shortageQty > 0 ? (
                <span
                  style={{ color: "#ef4444", fontSize: 12, fontWeight: 600 }}
                >
                  {bline.shortageQty} eksik
                </span>
              ) : (
                <span style={{ color: "#10b981", fontSize: 12 }}>
                  ✓ Yeterli
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Shortage = () => {
  const { getToken } = useAuth();
  useTitle("Eksik Malzeme Raporu");

  const [toasts, setToasts] = useState([]);

  // Plans
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPartId, setFilterPartId] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Feasibility
  const [feasibility, setFeasibility] = useState([]);
  const [feasibilityLoading, setFeasibilityLoading] = useState(false);
  const [feasibilityFilter, setFeasibilityFilter] = useState("");
  const [expandedFeasRow, setExpandedFeasRow] = useState(null);
  const [expandedVariantRow, setExpandedVariantRow] = useState(null);

  // Shortage
  const [shortageMode, setShortageMode] = useState("part"); // "part" | "product"
  const [isProductMode, setIsProductMode] = useState(true);
  const [viewMode, setViewMode] = useState("tree");
  const [hideSufficient, setHideSufficient] = useState(true);
  const [shortageMainPartId, setShortageMainPartId] = useState("");
  const [shortageVariantId, setShortageVariantId] = useState("");
  const [shortageModelId, setShortageModelId] = useState("");
  const [shortageVariants, setShortageVariants] = useState([]);
  const [shortageVariantsLoading, setShortageVariantsLoading] = useState(false);
  const [shortageQty, setShortageQty] = useState("");
  const [shortageResult, setShortageResult] = useState(null);
  const [shortageLoading, setShortageLoading] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState(new Set());

  const getFlatList = useCallback(() => {
    if (!shortageResult?.bomTree) return [];
    
    const partMap = new Map();

    function traverse(node) {
      if (node.partId !== "model-root") {
        if (!partMap.has(node.partId)) {
          partMap.set(node.partId, {
            partId: node.partId,
            partName: node.partName,
            partCode: node.partCode,
            neededQty: 0,
            availableQty: node.availableQty,
            sourceType: node.sourceType,
          });
        } else {
          partMap.get(node.partId).availableQty = Math.max(partMap.get(node.partId).availableQty, node.availableQty);
        }
        partMap.get(node.partId).neededQty += node.neededQty;
      }
      if (node.children) {
        node.children.forEach(traverse);
      }
    }

    traverse(shortageResult.bomTree);

    const list = Array.from(partMap.values()).map(p => {
      const shortageQty = Math.max(0, p.neededQty - p.availableQty);
      return { ...p, shortageQty, depth: 0, hasChildren: false };
    });

    if (hideSufficient) {
      return list.filter(p => p.shortageQty > 0);
    }
    return list;
  }, [shortageResult, hideSufficient]);

  useEffect(() => {
    // Make it closed by default
    setExpandedPaths(new Set());
  }, [shortageResult]);

  const getVisibleRows = useCallback(() => {
    if (!shortageResult?.bomTree) return [];
    const visible = [];

    function traverse(node, depth = 0, parentPath = "") {
      const currentPath = parentPath ? `${parentPath}/${node.partId}` : node.partId;
      const row = {
        ...node,
        depth,
        path: currentPath,
        hasChildren: node.children && node.children.length > 0,
      };

      if (node.partId !== "model-root") {
        visible.push(row);
      }

      const isModelRoot = node.partId === "model-root";
      const childDepth = isModelRoot ? 0 : depth + 1;
      const shouldTraverseChildren =
        isModelRoot ||
        (node.children && node.children.length > 0 && expandedPaths.has(currentPath));

      if (shouldTraverseChildren && node.children) {
        node.children.forEach((child) => traverse(child, childDepth, currentPath));
      }
    }

    traverse(shortageResult.bomTree, 0, "");
    return visible;
  }, [shortageResult, expandedPaths]);


  // Shared
  const [parts, setParts] = useState([]);
  const [lines, setLines] = useState([]);
  const [deviceModels, setDeviceModels] = useState([]);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${getToken()}` }),
    [getToken],
  );

  const addToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      4000,
    );
  }, []);

  const fetchParts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/parts`, {
        headers: authHeaders(),
      });
      const list = res.data?.parts ?? res.data?.data ?? [];
      setParts(Array.isArray(list) ? list : []);
    } catch {
      setParts([]);
    }
  }, [authHeaders]);

  const fetchLines = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/production-lines`, {
        headers: authHeaders(),
      });
      const list = res.data?.lines ?? res.data?.data ?? [];
      setLines(Array.isArray(list) ? list : []);
    } catch {
      setLines([]);
    }
  }, [authHeaders]);

  const fetchDeviceModels = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/device-models`, {
        headers: authHeaders(),
      });
      const list = res.data?.models ?? res.data?.deviceModels ?? res.data ?? [];
      setDeviceModels(Array.isArray(list) ? list : []);
    } catch {
      setDeviceModels([]);
    }
  }, [authHeaders]);

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPartId) params.partId = filterPartId;
      if (filterStartDate) params.startDate = filterStartDate;
      if (filterEndDate) params.endDate = filterEndDate;
      const res = await axios.get(`${API_URL}/api/production-planning/plans`, {
        headers: authHeaders(),
        params,
      });
      setPlans(res.data?.data || []);
    } catch {
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [authHeaders, filterStatus, filterPartId, filterStartDate, filterEndDate]);

  const fetchFeasibility = useCallback(async () => {
    setFeasibilityLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/production-planning/feasibility`,
        {
          headers: authHeaders(),
        },
      );
      setFeasibility(res.data?.data || []);
    } catch {
      setFeasibility([]);
    } finally {
      setFeasibilityLoading(false);
    }
  }, [authHeaders]);

  const fetchPartVariants = useCallback(
    async (mainId) => {
      if (!mainId) {
        setShortageVariants([]);
        setShortageVariantId("");
        return;
      }
      setShortageVariantsLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/parts/${mainId}`, {
          headers: authHeaders(),
        });
        const vars = res.data?.variants || [];
        setShortageVariants(vars);
        setShortageVariantId("");
      } catch {
        setShortageVariants([]);
        setShortageVariantId("");
      } finally {
        setShortageVariantsLoading(false);
      }
    },
    [authHeaders],
  );

  const fetchPlansRef = useRef(fetchPlans);
  const fetchFeasibilityRef = useRef(fetchFeasibility);
  fetchPlansRef.current = fetchPlans;
  fetchFeasibilityRef.current = fetchFeasibility;

  const handleTabChange = useCallback((tabIndex) => {
    if (tabIndex === 0) fetchPlansRef.current();
    else if (tabIndex === 1) fetchFeasibilityRef.current();
  }, []);

  useEffect(() => {
    fetchParts();
    fetchLines();
    fetchDeviceModels();
  }, [fetchParts, fetchLines, fetchDeviceModels]);

  const openCreate = () => {
    setEditingPlan(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };
  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      title: plan.title || "",
      partId: plan.partId?._id || plan.partId || "",
      productionLineId:
        plan.productionLineId?._id || plan.productionLineId || "",
      plannedQuantity: plan.plannedQuantity ?? "",
      startDate: plan.startDate ? plan.startDate.slice(0, 10) : "",
      endDate: plan.endDate ? plan.endDate.slice(0, 10) : "",
      operatorNote: plan.operatorNote || "",
      status: plan.status || "planned",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.startDate || !form.endDate) {
      addToast("Lütfen zorunlu alanları doldurun.", "error");
      return;
    }
    setFormLoading(true);
    try {
      const payload = {
        title: form.title,
        partId: form.partId || null,
        productionLineId: form.productionLineId || null,
        plannedQuantity: form.plannedQuantity
          ? Number(form.plannedQuantity)
          : 0,
        startDate: form.startDate,
        endDate: form.endDate,
        operatorNote: form.operatorNote,
        status: form.status,
      };
      if (editingPlan) {
        await axios.put(
          `${API_URL}/api/production-planning/plans/${editingPlan._id}`,
          payload,
          { headers: authHeaders() },
        );
        addToast("Plan güncellendi.");
      } else {
        await axios.post(`${API_URL}/api/production-planning/plans`, payload, {
          headers: authHeaders(),
        });
        addToast("Plan oluşturuldu.");
      }
      setModalOpen(false);
      fetchPlans();
    } catch (err) {
      addToast(err?.response?.data?.message || "Bir hata oluştu.", "error");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/production-planning/plans/${id}`, {
        headers: authHeaders(),
      });
      addToast("Plan silindi.");
      setDeleteConfirm(null);
      fetchPlans();
    } catch {
      addToast("Silme başarısız.", "error");
    }
  };

  const handleStatusChange = async (plan, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/production-planning/plans/${plan._id}/status`,
        { status: newStatus },
        { headers: authHeaders() },
      );
      addToast("Durum güncellendi.");
      fetchPlans();
    } catch {
      addToast("Durum güncellenemedi.", "error");
    }
  };

  const resetShortageForm = () => {
    setShortageMainPartId("");
    setShortageVariantId("");
    setShortageModelId("");
    setShortageVariants([]);
    setShortageResult(null);
  };

  const runShortage = async () => {
    const isProductMode = shortageMode === "product";
    const effectivePartId = shortageVariantId || shortageMainPartId;

    if (isProductMode ? !shortageModelId : !effectivePartId) {
      addToast(
        isProductMode ? "Model ve miktar seçin." : "Parça ve miktar seçin.",
        "error",
      );
      return;
    }
    if (!shortageQty) {
      addToast("Miktar girin.", "error");
      return;
    }

    setShortageLoading(true);
    setShortageResult(null);
    try {
      const payload = isProductMode
        ? { deviceModelId: shortageModelId, quantity: Number(shortageQty), ignoreStock: true }
        : { partId: effectivePartId, quantity: Number(shortageQty), ignoreStock: true };
      const res = await axios.post(
        `${API_URL}/api/production-planning/shortage`,
        payload,
        { headers: authHeaders() },
      );
      setShortageResult(res.data?.data || null);
    } catch (err) {
      addToast(err?.response?.data?.message || "Hesaplama hatası.", "error");
    } finally {
      setShortageLoading(false);
    }
  };

  const planStats = {
    total: plans.length,
    planned: plans.filter((p) => p.status === "planned").length,
    inProgress: plans.filter((p) => p.status === "in_progress").length,
    completed: plans.filter((p) => p.status === "completed").length,
  };

  const allParts = Array.isArray(parts) ? parts : [];
  const partOptions = allParts.map((p) => ({
    value: p._id,
    label: `${p.name} (${p.code})`,
  }));
  const shortagePartOptions = allParts
    .filter((p) => !p.isFinalProduct)
    .map((p) => ({
      value: p._id,
      label: `${p.name} (${p.code})`,
    }));
  const modelOptions = (Array.isArray(deviceModels) ? deviceModels : []).map(
    (m) => ({
      value: m._id,
      label: `${m.code} — ${m.name}`,
    }),
  );
  const lineOptions = (Array.isArray(lines) ? lines : []).map((l) => ({
    value: l._id,
    label: l.name || l.code,
  }));

  // Group feasibility: main parts as rows, variants as sub-rows.
  // Search matches any main part or any of its variants.
  const q = feasibilityFilter.toLowerCase();
  const feasMainItems = feasibility.filter((f) => !f.mainPartId);
  const feasVariantMap = feasibility.reduce((acc, f) => {
    if (f.mainPartId) {
      if (!acc[f.mainPartId]) acc[f.mainPartId] = [];
      acc[f.mainPartId].push(f);
    }
    return acc;
  }, {});

  const groupedFeasibility = feasMainItems
    .map((main) => ({
      ...main,
      variants: feasVariantMap[main.partId] || [],
    }))
    .filter((g) => {
      if (!q) return true;
      const matchesMain =
        g.partName?.toLowerCase().includes(q) ||
        g.partCode?.toLowerCase().includes(q);
      const matchesVariant = g.variants.some(
        (v) =>
          v.partName?.toLowerCase().includes(q) ||
          v.partCode?.toLowerCase().includes(q),
      );
      return matchesMain || matchesVariant;
    });

  // Also include parts that are variants but have no matching main (orphan variants shown standalone)
  const orphanVariants = feasibility.filter(
    (f) =>
      f.mainPartId &&
      !feasMainItems.find((m) => m.partId === f.mainPartId) &&
      (!q ||
        f.partName?.toLowerCase().includes(q) ||
        f.partCode?.toLowerCase().includes(q)),
  );

  const totalFeasCount =
    groupedFeasibility.length +
    groupedFeasibility.reduce((s, g) => s + g.variants.length, 0) +
    orphanVariants.length;

  // Shortage tab: variant options for selected main part
  const shortageVariantOptions = shortageVariants.map((v) => ({
    value: v._id,
    label: `${v.name} (${v.code})`,
  }));

  return (
    <AppPage
      breadcrumbs={[{ name: "Üretim Yönetimi" }, { name: "Eksik Malzeme Raporu" }]}
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast key={t.id} type={t.type}>
            {t.message}
          </Toast>
        ))}
      </ToastContainer>

      <div className="mlm-header card card-body p-3 mb-3">
        <div className="mlm-header-left">
          <h2 className="mlm-title">Eksik Malzeme Raporu</h2>
        </div>
      </div>
          {/* ── TAB 2: Eksik Malzeme Raporu ── */}
          <Panel>
            <Tabs
              onChange={(idx) => {
                const newMode = idx === 0 ? "part" : "product";
                if (shortageMode !== newMode) {
                  setShortageMode(newMode);
                  resetShortageForm();
                }
              }}
            >
              <TabList>
                <Tab>
                  <div className="d-flex align-items-center gap-1">
                    <FiPackage /> Parça Bazlı
                  </div>
                </Tab>
                <Tab>
                  <div className="d-flex align-items-center gap-1">
                    <FiCheckSquare /> Model Bazlı
                  </div>
                </Tab>
              </TabList>
              <TabPanel>
                <Panel>
                  <Container>
                    <ContainerItem cols={4}>
                      <Select
                        label="Ana Parça Seçin *"
                        options={[
                          { value: "", label: "Parça seçin..." },
                          ...shortagePartOptions,
                        ]}
                        selected={[shortageMainPartId]}
                        onSelected={(vals) => {
                          const id = vals?.[0]?.value ?? "";
                          setShortageMainPartId(id);
                          setShortageResult(null);
                          fetchPartVariants(id);
                        }}
                        unique
                        searchInDropdown
                        searchPlaceholder="Parça ara..."
                      />
                    </ContainerItem>
                    <ContainerItem cols={3}>
                      <Select
                        label="Varyant"
                        options={[
                          {
                            value: "",
                            label: shortageVariantsLoading
                              ? "Yükleniyor..."
                              : "Tüm varyantlar / Ana parça",
                          },
                          ...shortageVariantOptions,
                        ]}
                        selected={[shortageVariantId]}
                        onSelected={(vals) => {
                          const id = vals?.[0]?.value ?? "";
                          setShortageVariantId(id);
                          setShortageResult(null);
                        }}
                        unique
                        searchInDropdown
                        searchPlaceholder="Varyant ara..."
                        disabled={!shortageMainPartId || shortageVariantsLoading}
                      />
                    </ContainerItem>
                    <ContainerItem cols={3}>
                      <Input
                        label="Üretmek İstediğiniz Miktar *"
                        type="number"
                        value={shortageQty}
                        onChange={(val) => {
                          setShortageQty(val);
                          setShortageResult(null);
                        }}
                        placeholder="0"
                        min="1"
                      />
                    </ContainerItem>
                    <ContainerItem cols={2}>
                      <div style={{ paddingTop: 22 }}>
                        <Button
                          appearance="primary"
                          onClick={runShortage}
                          disabled={shortageLoading || !shortageQty || !shortageMainPartId}
                          style={{ width: "100%" }}
                        >
                          {shortageLoading ? <FiRefreshCw className="spin" /> : <FiSearch />} Hesapla
                        </Button>
                      </div>
                    </ContainerItem>
                  </Container>
                </Panel>
                <Panel>
                  <Container>
                    <ContainerItem cols={7}>
                      <Select
                        label="Model Seçin *"
                        options={[
                          { value: "", label: "Model seçin..." },
                          ...modelOptions,
                        ]}
                        selected={[shortageModelId]}
                        onSelected={(vals) => {
                          setShortageModelId(vals?.[0]?.value ?? "");
                          setShortageResult(null);
                        }}
                        unique
                        searchInDropdown
                        searchPlaceholder="Model ara..."
                      />
                    </ContainerItem>
                    <ContainerItem cols={3}>
                      <Input
                        label="Üretmek İstediğiniz Miktar *"
                        type="number"
                        value={shortageQty}
                        onChange={(val) => {
                          setShortageQty(val);
                          setShortageResult(null);
                        }}
                        placeholder="0"
                        min="1"
                      />
                    </ContainerItem>
                    <ContainerItem cols={2}>
                      <div style={{ paddingTop: 22 }}>
                        <Button
                          appearance="primary"
                          onClick={runShortage}
                          disabled={shortageLoading || !shortageQty || !shortageModelId}
                          style={{ width: "100%" }}
                        >
                          {shortageLoading ? <FiRefreshCw className="spin" /> : <FiSearch />} Hesapla
                        </Button>
                      </div>
                    </ContainerItem>
                  </Container>
                </Panel>
              </TabPanel>
            </Tabs>

            {shortageResult && (
              <>
                <Container className={styles.statsRow}>
                  <ContainerItem className={styles.statsCard}>
                    <CardInfo
                      card={{
                        title: "İstenen Miktar",
                        value: shortageResult.requestedQty,
                        icon: <FiTrendingUp size={20} />,
                      }}
                      appearance="primary"
                    />
                  </ContainerItem>
                  <ContainerItem className={styles.statsCard}>
                    <CardInfo
                      card={{
                        title: "Üretilebilir",
                        value: shortageResult.maxProducible ?? 0,
                        icon: <FiPackage size={20} />,
                      }}
                      appearance={
                        shortageResult.canFulfill ? "success" : "warning"
                      }
                    />
                  </ContainerItem>
                  <ContainerItem className={styles.statsCard}>
                    <CardInfo
                      card={{
                        title: "Eksik Bileşen",
                        value: shortageResult.summary?.missingComponents ?? 0,
                        icon: <FiAlertCircle size={20} />,
                      }}
                      appearance={
                        (shortageResult.summary?.missingComponents ?? 0) > 0
                          ? "danger"
                          : "success"
                      }
                    />
                  </ContainerItem>
                  <ContainerItem className={styles.statsCard}>
                    <CardInfo
                      card={{
                        title: "Yeterli Bileşen",
                        value:
                          shortageResult.summary?.sufficientComponents ?? 0,
                        icon: <FiCheckSquare size={20} />,
                      }}
                      appearance="success"
                    />
                  </ContainerItem>
                </Container>

                <Container>
                  <ContainerItem>
                    <div
                      className={styles.feasBanner}
                      style={{
                        borderColor: shortageResult.canFulfill
                          ? "#10b981"
                          : "#ef4444",
                        background: shortageResult.canFulfill
                          ? "#f0fdf4"
                          : "#fff5f5",
                      }}
                    >
                      <div
                        style={{
                          color: shortageResult.canFulfill
                            ? "#10b981"
                            : "#ef4444",
                          flexShrink: 0,
                        }}
                      >
                        {shortageResult.canFulfill ? (
                          <FiCheckSquare size={26} />
                        ) : (
                          <FiAlertCircle size={26} />
                        )}
                      </div>
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 14,
                            color: shortageResult.canFulfill
                              ? "#166534"
                              : "#991b1b",
                              marginBottom: "10px"
                          }}
                        >
                          {shortageResult.canFulfill
                            ? `${shortageResult.requestedQty} adet ${
                                shortageResult.deviceModel
                                  ? `${shortageResult.deviceModel.code} modeli`
                                  : shortageResult.partName
                              } üretimi mevcut stokla karşılanabilir.`
                            : `${shortageResult.requestedQty} adet ${
                                shortageResult.deviceModel
                                  ? `${shortageResult.deviceModel.code} modeli`
                                  : shortageResult.partName
                              } üretimi için stok yetersiz.`}
                        </div>
                        {shortageResult.deviceModel && (
                          <div
                            style={{
                              fontSize: 12,
                              marginTop: 4,
                              color: "var(--m-text-subtle, #6b7280)",
                            }}
                          >
                            Model:{" "}
                            <strong>
                              {shortageResult.deviceModel.code} —{" "}
                              {shortageResult.deviceModel.name}
                            </strong>
                            {" · "}Nihai parça:{" "}
                            <strong>
                              {shortageResult.partName} ({shortageResult.partCode})
                            </strong>
                          </div>
                        )}
                        {!shortageResult.canFulfill &&
                          shortageResult.maxProducible > 0 && (
                            <div
                              style={{
                                fontSize: 13,
                                marginTop: 4,
                                color: "#92400e",
                              }}
                            >
                              Mevcut stokla en fazla{" "}
                              <strong>{shortageResult.maxProducible}</strong>{" "}
                              adet üretilebilir.{" "}
                              <strong>
                                {shortageResult.requestedQty -
                                  shortageResult.maxProducible}
                              </strong>{" "}
                              adet eksik.
                            </div>
                          )}
                        {shortageResult.maxProducible === 0 &&
                          shortageResult.bomSource !== "none" && (
                            <div
                              style={{
                                fontSize: 13,
                                marginTop: 4,
                                color: "#991b1b",
                              }}
                            >
                              Hiç üretim yapılamaz — bileşenler stokta mevcut
                              değil.
                            </div>
                          )}
                      </div>
                    </div>
                  </ContainerItem>
                </Container>

                 {shortageResult.bomTree ? (
                  <Container>
                    <ContainerItem>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div style={{ flex: 1 }}>
                          <Tabs
                            selected={viewMode === "tree" ? 1 : 2}
                            onChange={(i) => setViewMode(i === 0 ? "tree" : "list")}
                          >
                            <TabList>
                              <Tab>Ağaç Görünümü</Tab>
                              <Tab>Liste Görünümü (Toplam İhtiyaç)</Tab>
                            </TabList>
                          </Tabs>
                        </div>
                        {viewMode === "list" && (
                          <div style={{ marginLeft: "1rem" }}>
                            <Checkbox
                              label="Yeterli Olanları Gizle"
                              isChecked={hideSufficient}
                              onChange={({ checked }) => setHideSufficient(checked)}
                            />
                          </div>
                        )}
                      </div>
                      <Table columns={SHORTAGE_COLUMNS} search={false}>
                        {viewMode === "tree" ? (
                          getVisibleRows().map((bline, i) => {
                            const isSonUrun = bline.depth === 0 && (!shortageResult.deviceModel || bline.sourceType === "finalProduct");
                            const isExpanded = expandedPaths.has(bline.path);
                            return (
                              <Tr
                                key={i}
                                style={{
                                  cursor: bline.hasChildren ? "pointer" : "default",
                                  background: (bline.shortageQty > 0 && !isSonUrun) ? "#fff8f8" : (isSonUrun ? "#f0fdf4" : undefined),
                                }}
                                onClick={(e) => {
                                  if (bline.hasChildren) {
                                    e.stopPropagation();
                                    const newPaths = new Set(expandedPaths);
                                    if (isExpanded) {
                                      newPaths.delete(bline.path);
                                    } else {
                                      newPaths.add(bline.path);
                                    }
                                    setExpandedPaths(newPaths);
                                  }
                                }}
                              >
                                <Td>
                                  <div
                                    className="d-flex align-items-center gap-2 flex-wrap"
                                    style={{ paddingLeft: bline.depth * 20 }}
                                  >
                                    {bline.hasChildren && (
                                      <span
                                        style={{
                                          color: "var(--m-text-subtle, #9ca3af)",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          marginRight: 4,
                                        }}
                                      >
                                        {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                                      </span>
                                    )}
                                    {!bline.hasChildren && bline.depth > 0 && (
                                      <span style={{ width: 20, display: "inline-block" }} />
                                    )}
                                    <strong>{bline.partName}</strong>
                                    {bline.sourceType && (
                                      <span
                                        className={styles.chip}
                                        style={{
                                          background:
                                            bline.sourceType === "finalProduct"
                                              ? "#ede9fe"
                                              : bline.sourceType === "partAssembly"
                                                ? "#fef3c7"
                                                : "#dbeafe",
                                          color:
                                            bline.sourceType === "finalProduct"
                                              ? "#7c3aed"
                                              : bline.sourceType === "partAssembly"
                                                ? "#92400e"
                                                : "#1d4ed8",
                                        }}
                                      >
                                        {BOM_SOURCE_LABELS[bline.sourceType] ||
                                          bline.sourceType}
                                      </span>
                                    )}
                                  </div>
                                </Td>
                                <Td
                                  style={{
                                    color: "var(--m-text-subtle, #9ca3af)",
                                    fontSize: 12,
                                  }}
                                >
                                  {bline.partCode}
                                </Td>
                                <Td>{bline.consumptionRatio}</Td>
                                <Td>
                                  <strong>{bline.neededQty}</strong>
                                </Td>
                                <Td>
                                  {isSonUrun ? <span style={{ color: "#6b7280" }}>—</span> : (
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color:
                                          bline.availableQty >= bline.neededQty
                                            ? "#10b981"
                                            : "#ef4444",
                                      }}
                                    >
                                      {bline.availableQty}
                                    </span>
                                  )}
                                </Td>
                                <Td>
                                  {isSonUrun ? <span style={{ color: "#6b7280" }}>—</span> : (
                                    bline.shortageQty > 0 ? (
                                      <span
                                        style={{
                                          color: "#ef4444",
                                          fontWeight: 700,
                                        }}
                                      >
                                        {bline.shortageQty}
                                      </span>
                                    ) : (
                                      <span style={{ color: "#10b981" }}>—</span>
                                    )
                                  )}
                                </Td>
                                <Td>
                                  {isSonUrun ? (
                                    <span style={{ color: "#3b82f6", fontSize: 12, fontWeight: 600 }}>
                                      Üretilecek
                                    </span>
                                  ) : bline.shortageQty > 0 ? (
                                    <span
                                      style={{
                                        color: "#ef4444",
                                        fontSize: 12,
                                        fontWeight: 600,
                                      }}
                                    >
                                      <FiAlertCircle
                                        size={12}
                                        style={{ marginRight: 4 }}
                                      />
                                      Eksik
                                    </span>
                                  ) : (
                                    <span
                                      style={{ color: "#10b981", fontSize: 12 }}
                                    >
                                      <FiCheckSquare
                                        size={12}
                                        style={{ marginRight: 4 }}
                                      />
                                      Yeterli
                                    </span>
                                  )}
                                </Td>
                              </Tr>
                            );
                          })
                        ) : (
                          getFlatList().map((bline, i) => {
                            const isSonUrun = bline.depth === 0 && (!shortageResult.deviceModel || bline.sourceType === "finalProduct");
                            return (
                              <Tr
                                key={i}
                                style={{
                                  background: (bline.shortageQty > 0 && !isSonUrun) ? "#fff8f8" : (isSonUrun ? "#f0fdf4" : undefined),
                                }}
                              >
                                <Td>
                                  <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <strong>{bline.partName}</strong>
                                  </div>
                                </Td>
                                <Td
                                  style={{
                                    color: "var(--m-text-subtle, #9ca3af)",
                                    fontSize: 12,
                                  }}
                                >
                                  {bline.partCode}
                                </Td>
                                <Td><span style={{ color: "#6b7280" }}>—</span></Td>
                                <Td>
                                  <strong>{bline.neededQty}</strong>
                                </Td>
                                <Td>
                                  {isSonUrun ? <span style={{ color: "#6b7280" }}>—</span> : (
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color:
                                          bline.availableQty >= bline.neededQty
                                            ? "#10b981"
                                            : "#ef4444",
                                      }}
                                    >
                                      {bline.availableQty}
                                    </span>
                                  )}
                                </Td>
                                <Td>
                                  {isSonUrun ? <span style={{ color: "#6b7280" }}>—</span> : (
                                    bline.shortageQty > 0 ? (
                                      <span
                                        style={{
                                          color: "#ef4444",
                                          fontWeight: 700,
                                        }}
                                      >
                                        {bline.shortageQty}
                                      </span>
                                    ) : (
                                      <span style={{ color: "#10b981" }}>—</span>
                                    )
                                  )}
                                </Td>
                                <Td>
                                  {isSonUrun ? (
                                    <span style={{ color: "#3b82f6", fontSize: 12, fontWeight: 600 }}>
                                      Üretilecek
                                    </span>
                                  ) : bline.shortageQty > 0 ? (
                                    <span
                                      style={{
                                        color: "#ef4444",
                                        fontSize: 12,
                                        fontWeight: 600,
                                      }}
                                    >
                                      <FiAlertCircle
                                        size={12}
                                        style={{ marginRight: 4 }}
                                      />
                                      Eksik
                                    </span>
                                  ) : (
                                    <span
                                      style={{ color: "#10b981", fontSize: 12 }}
                                    >
                                      <FiCheckSquare
                                        size={12}
                                        style={{ marginRight: 4 }}
                                      />
                                      Yeterli
                                    </span>
                                  )}
                                </Td>
                            </Tr>
                          );
                        }))}
                      </Table>
                    </ContainerItem>
                  </Container>
                ) : null}
              </>
            )}
          </Panel>
    </AppPage>
  );
};

export default withAuth(Shortage, ["admin", "employee"]);
